import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Package, Tag, Star, Download, Filter, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { API } from '../config';


const labels = {
  en: {
    title: 'Marketplace',
    subtitle: 'Extend HexoPDF with community plugins and modules.',
    search: 'Search modules...',
    all: 'All',
    free: 'Free',
    empty: 'No modules available yet. Be the first to submit one!',
    submit: 'Submit a Module',
    by: 'by',
  },
  fr: {
    title: 'Marketplace',
    subtitle: 'Étendez HexoPDF avec des plugins et modules communautaires.',
    search: 'Rechercher un module...',
    all: 'Tous',
    free: 'Gratuit',
    empty: 'Aucun module disponible. Soyez le premier à en soumettre un !',
    submit: 'Soumettre un module',
    by: 'par',
  },
  hi: {
    title: 'मार्केटप्लेस',
    subtitle: 'सामुदायिक प्लगइन्स और मॉड्यूल के साथ HexoPDF का विस्तार करें।',
    search: 'मॉड्यूल खोजें...',
    all: 'सभी',
    free: 'मुफ़्त',
    empty: 'अभी कोई मॉड्यूल उपलब्ध नहीं। पहले बनने वाले बनें!',
    submit: 'मॉड्यूल सबमिट करें',
    by: 'द्वारा',
  },
};

const categoryLabels = {
  utility: { en: 'Utility', fr: 'Utilitaire', hi: 'उपयोगिता' },
  ocr: { en: 'Text extraction (OCR)', fr: 'Extraction de texte (OCR)', hi: 'पाठ निष्कर्षण (OCR)' },
  converter: { en: 'Converter', fr: 'Convertisseur', hi: 'कनवर्टर' },
  template: { en: 'Templates', fr: 'Modèles', hi: 'टेम्पलेट' },
  security: { en: 'Security', fr: 'Sécurité', hi: 'सुरक्षा' },
  annotation: { en: 'Annotation', fr: 'Annotation', hi: 'एनोटेशन' },
  integration: { en: 'Integration', fr: 'Intégration', hi: 'इंटीग्रेशन' },
};

export default function MarketplacePage() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const [modules, setModules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const l = labels[lang] || labels.en;

  useEffect(() => {
    const params = selectedCat !== 'all' ? `?category=${selectedCat}` : '';
    axios.get(`${API}/marketplace${params}`)
      .then(r => {
        setModules(r.data.modules || []);
        setCategories(r.data.categories || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedCat]);

  const filtered = modules.filter(m =>
    m.name.toLowerCase().includes(search.toLowerCase()) ||
    m.description.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background" data-testid="marketplace-page">
      <Navbar />
      <div className="pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
            <div className="flex items-start justify-between gap-4">
              <div>
                <span className="text-xs uppercase tracking-[0.2em] text-blue-400/70 font-medium">Marketplace</span>
                <h1 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-foreground mt-3" data-testid="marketplace-title">{l.title}</h1>
                <p className="text-muted-foreground text-lg mt-3 max-w-xl">{l.subtitle}</p>
              </div>
              {user && (
                <Link to="/marketplace/submit">
                  <Button className="bg-blue-600 hover:bg-blue-500 text-white rounded-lg shrink-0 mt-4" data-testid="marketplace-submit-btn">
                    <Plus className="w-4 h-4 mr-1.5" />{l.submit}
                  </Button>
                </Link>
              )}
            </div>
          </motion.div>

          {/* Search + Filter */}
          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={l.search}
                className="pl-10 bg-card border-border text-foreground h-10 rounded-lg"
                data-testid="marketplace-search"
              />
            </div>
            <div className="flex gap-1.5 flex-wrap">
              <button
                onClick={() => setSelectedCat('all')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedCat === 'all' ? 'bg-blue-600 text-white' : 'bg-secondary text-muted-foreground hover:text-white'}`}
                data-testid="marketplace-filter-all"
              >
                {l.all}
              </button>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCat(cat)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedCat === cat ? 'bg-blue-600 text-white' : 'bg-secondary text-muted-foreground hover:text-white'}`}
                  data-testid={`marketplace-filter-${cat}`}
                >
                  {categoryLabels[cat]?.[lang] || cat}
                </button>
              ))}
            </div>
          </div>

          {/* Module Grid */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-muted-foreground text-center py-20" data-testid="marketplace-empty">{l.empty}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((mod, i) => (
                <motion.div
                  key={mod.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                >
                  <Link
                    to={`/marketplace/${mod.slug}`}
                    className="group block p-5 rounded-2xl bg-card border border-border hover:border-blue-500/20 transition-all h-full"
                    data-testid={`marketplace-card-${mod.slug}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <Package className="w-8 h-8 text-blue-400 shrink-0" />
                      <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
                        {categoryLabels[mod.category]?.[lang] || mod.category}
                      </span>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground group-hover:text-blue-400 transition-colors mb-1.5">{mod.name}</h3>
                    <p className="text-muted-foreground text-sm line-clamp-2 mb-3">{mod.short_description || mod.description}</p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{l.by} {mod.creator_name}</span>
                      <span className={mod.price === 0 ? 'text-emerald-400 font-medium' : 'text-white font-medium'}>
                        {mod.price === 0 ? l.free : `${mod.price} ${mod.currency?.toUpperCase()}`}
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
